import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DeleteModal from "../../../components/DeleteModal";
import {
  ActionsCell,
  ClickableRow,
  EmptyState,
  IconButton,
  Page,
  PageHeader,
  Pagination,
  SearchInput,
  TableSkeleton,
  usePagination,
} from "../../../components/ui";
import {
  capitalize,
  matches,
  newestFirst,
  stripHtml,
} from "../../../utils/list";
import { canEditContent } from "../../../utils/roles";

/**
 * Paginated list of season-based contents (TV shows, podcasts).
 *
 * - `fetchItems()`: resolves with the list of items
 * - `deleteItem(id)`: delete request used by the confirmation modal
 * - `paths`: { add, view(id), episodes(id), edit(id) }
 * - `labels`: translation keys { title, count, add, search, column, confirmDelete }
 * - `section`: permission section ("series" or "podcasts")
 */
const SeriesListPage = ({
  fetchItems,
  deleteItem,
  paths,
  labels,
  emptyIcon,
  section = "series",
}) => {
  const canEdit = canEditContent(section);
  const history = useHistory();
  const { t } = useTranslation();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [itemToDelete, setItemToDelete] = useState(null);

  const loadItems = () =>
    fetchItems().then((list) => {
      setItems(newestFirst(list) || []);
      setLoading(false);
    });

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(
    () => items.filter((item) => matches(item?.title, search)),
    [items, search]
  );
  const pagination = usePagination(filtered, 10, [search]);

  return (
    <Page>
      <PageHeader
        title={t(labels.title)}
        subtitle={loading ? " " : `${items.length} ${t(labels.count)}`}
        actions={
          canEdit && (
            <Link to={paths.add} className="ui-btn">
              <i className="ri-add-line" aria-hidden="true"></i>
              {t(labels.add)}
            </Link>
          )
        }
      />

      <section className="ui-card">
        <div className="ui-toolbar">
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder={t(labels.search)}
          />
        </div>

        {loading ? (
          <TableSkeleton />
        ) : filtered.length === 0 ? (
          <EmptyState icon={emptyIcon}>{t("No Data Found")}</EmptyState>
        ) : (
          <>
            <div className="ui-table-wrap">
              <table className="ui-table">
                <thead>
                  <tr>
                    <th>{t(labels.column)}</th>
                    <th>{t("seasons")}</th>
                    <th>{t("desc")}</th>
                    <th className="ui-cell-actions">
                      <span className="sr-only">{t("action")}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {pagination.pageItems.map((item) => {
                    const description = stripHtml(item?.description);
                    return (
                      <ClickableRow key={item?._id} to={paths.view(item?._id)}>
                        <td>
                          <div className="ui-media">
                            <img
                              className="ui-media-thumb"
                              src={item?.thumbnail?.static_thumbnail_url || ""}
                              alt=""
                              loading="lazy"
                            />
                            <div style={{ minWidth: 0 }}>
                              <Link
                                to={paths.view(item?._id)}
                                className="ui-media-title"
                                title={item?.title}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {item?.title}
                              </Link>
                              <span className="ui-media-meta">
                                {capitalize(item?.default_language)}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="ui-cell-muted">
                          {t("seasonCount", { count: item?.seasons?.length || 0 })}
                        </td>
                        <td>
                          <span
                            className="ui-clamp"
                            style={{ maxWidth: 320 }}
                            title={description}
                          >
                            {description}
                          </span>
                        </td>
                        <ActionsCell>
                          <IconButton
                            icon="ri-eye-line"
                            label={t("View")}
                            onClick={() => history.push(paths.view(item?._id))}
                          />
                          <IconButton
                            icon="ri-play-list-line"
                            label={t("View Episodes")}
                            onClick={() =>
                              history.push(paths.episodes(item?._id))
                            }
                          />
                          {canEdit && (
                            <>
                              <IconButton
                                icon="ri-pencil-line"
                                label={t("Edit")}
                                onClick={() =>
                                  history.push(paths.edit(item?._id))
                                }
                              />
                              <IconButton
                                icon="ri-delete-bin-line"
                                label={t("Delete")}
                                tone="danger"
                                onClick={() => setItemToDelete(item?._id)}
                              />
                            </>
                          )}
                        </ActionsCell>
                      </ClickableRow>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Pagination {...pagination} onChange={pagination.setPage} />
          </>
        )}
      </section>

      {itemToDelete && (
        <DeleteModal
          key={itemToDelete}
          open={true}
          setOpen={setItemToDelete}
          seasonId={itemToDelete}
          deleteFunction={deleteItem}
          onSeasonDelete={loadItems}
        >
          <div className="model-wrapper">{t(labels.confirmDelete)}</div>
        </DeleteModal>
      )}
    </Page>
  );
};

export default SeriesListPage;
